<script lang="ts">
import { useBenefitsStore } from '@/stores/benefits'
import { usePlansStore } from '@/stores/plans'
import type { Benefit } from '@/types'
import { capitalCase } from 'change-case'
import { type PropType, defineComponent } from 'vue'

import BenefitButtons from '../inputs/BenefitButtons.vue'

/**
 * A card to display a benefit.
 *
 */
export default defineComponent({
  components: {
    BenefitButtons,
  },
  props: {
    /**
     * A configured benefit object.
     */
    benefit: {
      type: Object as PropType<Benefit>,
      required: true,
    },
    /**
     * Disables the redemption button.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const plansStore = usePlansStore()
    const benefitsStore = useBenefitsStore()

    return {
      benefitsStore,
      capitalCase,
      plansStore,
    }
  },
  computed: {
    isAvailable(): boolean {
      return this.benefitsStore.isAvailableInPlan(this.benefit)
    },
  },
  methods: {
    planBadgeColour(planTier?: string): string {
      switch (planTier) {
        case 'vip':
          return 'bg-tertiary'

        case 'platinum':
          return 'bg-black'

        default:
          return 'bg-primary'
      }
    },
  },
})
</script>

<template>
  <div
    class="card benefit-card !h-auto min-w-[288px] !max-w-[288px] flex-grow flex-col rounded-lg border border-slate-300 bg-pure-white"
  >
    <img
      :src="benefit.image"
      :title="benefit.title"
      class="aspect-square w-full"
      loading="lazy"
      width="288"
    />

    <div class="flex flex-grow flex-col gap-2 px-5 py-6">
      <span class="mb-4">
        <p class="text-lg font-semibold">
          {{ benefit.title }}
        </p>

        <p class="flex flex-grow leading-5">
          {{ benefit.short_description ?? benefit.description }}
        </p>
      </span>

      <div v-if="!isAvailable" class="mb-4 mt-auto flex justify-between">
        <div class="text-sm font-bold">Available with:</div>
        <div class="flex gap-2">
          <template v-if="benefit.plans.length">
            <AppBadge
              v-for="(plan, key) in benefitsStore.benefitPlans(benefit)"
              :key
              size="md"
              class="uppercase text-white"
              :class="planBadgeColour(plan.tier)"
            >
              {{ $t('membership_badge.' + plan.tier.toLocaleLowerCase()) }}
            </AppBadge>
          </template>
          <template v-if="!benefit.plans.length">
            <AppBadge
              size="md"
              class="uppercase text-white"
              :class="planBadgeColour()"
            >
              All
            </AppBadge>
          </template>
        </div>
      </div>

      <template v-if="$userStore.isLoggedIn && benefit.redemption_category">
        <BenefitButtons :benefit="benefit" :disabled="disabled" />
      </template>

      <template v-if="$userStore.isLoggedOut || !$userStore.hasSubscription">
        <AppButton
          :route="{
            name: 'memberships',
          }"
          class="button-secondary button-secondary-light mt-auto min-w-full font-semibold"
          title="Join to claim"
        >
          Join to claim
        </AppButton>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.benefit-card {
  > img:first-child {
    @apply rounded-lg rounded-b-none;
  }

  > div:last-child {
    @apply rounded-lg rounded-t-none;
  }
}
</style>
